import { Feature } from 'geojson'
import React, { FunctionComponent, useCallback, useEffect, useRef } from 'react'
import Map, {
  FullscreenControl,
  GeoJSONSource,
  Layer,
  MapMouseEvent,
  MapRef,
  Point,
  Source,
} from 'react-map-gl'
import { useDispatch } from 'react-redux'

import { MAPBOX_ACCESS_TOKEN } from '../../../../ini.json'
import {
  SubjectFeatureCollection,
  SubjectSearchApiResponse,
} from '../../../common/subjectTypes'
import {
  resetMapHoveredId,
  resetMapPopupData,
  setMapHoveredId,
  toggleMapPopupData,
} from '../../../redux/mapSlice'
import store from '../../../redux/store'
import {
  initialViewState,
  mapContainerStyle,
  subjectPaint,
  subjectPaintHiglighted,
} from '../../utils/mapConst'
import { SubjectSearchResultMapPopup } from '../SubjectSearchResultMapPopup/SubjectSearchResultMapPopup'

export interface SubjectSearchResultMapProps {
  dataSource: SubjectFeatureCollection
}

const interactiveLayerIds = ['points-layer', 'highlighted-point-layer']
const highlightedGeoJSON = {
  type: 'FeatureCollection' as const,
  features: [],
}

export const SubjectSearchResultMap: FunctionComponent<SubjectSearchResultMapProps> =
  ({ dataSource }) => {
    const dispatch = useDispatch()

    const mapRef = useRef<MapRef>(null)

    const mapRefCallback = useCallback((map: MapRef | null) => {
      if (map === null) return
      // @ts-expect-error This is soonest we can get the map object
      mapRef.current = map
    }, [])

    const handleMapHover = useCallback(
      (event: MapMouseEvent) => {
        const canvas = mapRef.current?.getCanvas()
        const feature = event.features?.[0]
        if (canvas) {
          if (feature) {
            canvas.style.cursor = 'pointer'
            dispatch(setMapHoveredId(feature.properties.id))
          } else {
            canvas.style.cursor = ''
            dispatch(resetMapHoveredId())
          }
        }
      },
      [dispatch]
    )

    const handleMouseLeave = useCallback(() => {
      const canvas = mapRef.current?.getCanvas()
      if (canvas) canvas.style.cursor = ''

      dispatch(resetMapHoveredId())
    }, [dispatch])

    // imperative subscribe is used to avoid re-rendering map on every hover
    useEffect(() => {
      const unsub = store.subscribe(() => {
        const id = store.getState().map.hoveredId
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const src = mapRef.current?.getSource<GeoJSONSource>(
          'highlighted-point-source'
        )
        if (src) {
          src.setData({
            type: 'FeatureCollection',
            features: id
              ? (dataSource.features.filter(
                  f => f.properties.id === id
                ) as Feature<Point, SubjectSearchApiResponse>[])
              : [],
          })
        }
      })
      return unsub
    }, [dataSource])

    const onClick = useCallback(
      (event: MapMouseEvent) => {
        const feature = event.features?.[0]
        if (!feature) {
          dispatch(resetMapPopupData())
          return
        }
        dispatch(
          toggleMapPopupData({
            type: 'Feature',
            geometry: feature.geometry,
            properties: feature.properties,
          })
        )
      },
      [dispatch]
    )

    return (
      <div
        className="w-full max-h-screen h-[600px] sticky top-6"
        id="subjectSearchMap"
      >
        <Map
          initialViewState={initialViewState}
          style={mapContainerStyle}
          mapStyle="mapbox://styles/kolpav/clyopvz7f001w01ph85m10qns"
          pitchWithRotate={false}
          touchZoomRotate={false}
          dragRotate={false}
          mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
          onClick={onClick}
          ref={mapRefCallback}
          interactiveLayerIds={interactiveLayerIds}
          onMouseMove={handleMapHover}
          onMouseLeave={handleMouseLeave}
        >
          <Source id="points-source" type="geojson" data={dataSource}>
            <Layer id="points-layer" type="circle" paint={subjectPaint} />
          </Source>

          <Source
            id="highlighted-point-source"
            type="geojson"
            data={highlightedGeoJSON}
          >
            <Layer
              id="highlighted-point-layer"
              type="circle"
              paint={subjectPaintHiglighted}
            />
          </Source>

          <SubjectSearchResultMapPopup />

          <FullscreenControl position="bottom-left" />
        </Map>
      </div>
    )
  }
